import React, { createContext, useContext, useState, useEffect } from 'react';

interface LoadingContextType {
  isLoading: boolean;
  loadingText: string;
  showLoader: (text?: string) => void;
  hideLoader: () => void;
  setLoadingText: (text: string) => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

interface LoadingProviderProps {
  children: React.ReactNode;
}

export const LoadingProvider: React.FC<LoadingProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false); // Start with loading false
  const [loadingText, setLoadingText] = useState('Loading');

  // Only show loader on very first app load (authentication/login)
  useEffect(() => {
    const isAuthRoute = window.location.pathname === '/' || window.location.pathname === '/login';
    const hasAuthToken = localStorage.getItem('authToken');
    
    // Only show initial loader for authentication flow
    if (isAuthRoute || !hasAuthToken) {
      const hasLoadedBefore = sessionStorage.getItem('appHasLoaded');
      
      if (!hasLoadedBefore) {
        setIsLoading(true);
        const timer = setTimeout(() => {
          setIsLoading(false);
          sessionStorage.setItem('appHasLoaded', 'true');
        }, 1500); // Show loader for 1.5 seconds on initial load

        return () => clearTimeout(timer);
      }
    }
  }, []);

  const showLoader = (text: string = 'Loading...') => {
    setLoadingText(text);
    setIsLoading(true);
  };

  const hideLoader = () => {
    setIsLoading(false);
  };

  const updateLoadingText = (text: string) => {
    setLoadingText(text);
  };

  const value = {
    isLoading,
    loadingText,
    showLoader,
    hideLoader,
    setLoadingText: updateLoadingText,
  };

  return (
    <LoadingContext.Provider value={value}>
      {children}
    </LoadingContext.Provider>
  );
};